import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ModulesApi, ProgramsApi } from 'src/api/client'
import {
  FETCH_ENGAGEMENTS_FOR_PROGRAM,
  FETCH_MODULE,
  FETCH_MODULES
} from 'src/constants/queries'
import {
  IEngagement,
  IEngagementFormData,
  IModule,
  IModuleFormData
} from 'src/models/module'
import { logger } from 'src/utils/logger'

export const useGetModules = () => {
  const query = useQuery<IModule[]>(FETCH_MODULES, () =>
    ModulesApi.getModules()
  )

  return { ...query, modules: query.data }
}

export const useGetModule = (id: string) => {
  const query = useQuery<IModule>(FETCH_MODULE(id), () =>
    ModulesApi.getModule(id)
  )

  return { ...query, module: query.data }
}

export const useGetEngagementsForProgram = (programId: string) => {
  const query = useQuery<{ version?: number; engagements: IEngagement[] }>(
    FETCH_ENGAGEMENTS_FOR_PROGRAM(programId),
    () => ProgramsApi.getProgramEngagementsv2(programId)
  )

  return { ...query, engagementsObject: query.data }
}

/**
 * ----- Mutations -----
 */

export const useCreateModule = ({
  onSuccess
}: {
  onSuccess?: (data: string) => void
} = {}) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (data: IModuleFormData) => ModulesApi.createModule(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(FETCH_MODULES)

        if (onSuccess) onSuccess(data)
      },
      onError: (err) => {
        logger.error('Error creating module', err)
      }
    }
  )

  return { ...mutation, createModule: mutation.mutate }
}

export const useUpdateModule = (
  moduleId: string,
  {
    onSuccess
  }: {
    onSuccess?: () => void
  } = {}
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (data: IModuleFormData) => ModulesApi.putModule(moduleId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_MODULE(moduleId))
        queryClient.invalidateQueries(FETCH_MODULES)

        if (onSuccess) onSuccess()
      },
      onError: (err) => {
        logger.error('Error updating module', err)
      }
    }
  )

  return { ...mutation, updateModule: mutation.mutate }
}

export const useUpdateModulesForProgram = (
  programId: string,
  {
    onSuccess,
    onError
  }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}
) => {
  const mutation = useMutation(
    (moduleIds: string[]) =>
      ModulesApi.putModulesForProgram(programId, moduleIds),
    {
      onSuccess: () => {
        if (onSuccess) onSuccess()
      },
      onError: (err: Error) => {
        logger.error('Error updating modules for program', err)
        if (onError) onError(err)
      }
    }
  )

  return { ...mutation, updateModulesForProgram: mutation.mutate }
}

export const useUpdateEngagementsForProgram = (
  programId: string,
  {
    onSuccess,
    onError
  }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    ({
      engagements,
      version
    }: {
      engagements: IEngagementFormData[]
      version: number
    }) =>
      ProgramsApi.updateProgramEngagementsv2(programId, engagements, version),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_ENGAGEMENTS_FOR_PROGRAM(programId))

        if (onSuccess) onSuccess()
      },
      onError: (err: Error) => {
        logger.error('Error updating engagements for program', err)
        if (onError) onError(err)
      }
    }
  )

  return { ...mutation, updateEngagementsForProgram: mutation.mutate }
}

export const useSyncEngagementsForProgram = (
  programId: string,
  {
    onSuccess,
    onError
  }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}
) => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    ({ version }: { version: number }) =>
      ProgramsApi.syncEngagements(programId, version),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_ENGAGEMENTS_FOR_PROGRAM(programId))

        if (onSuccess) onSuccess()
      },
      onError: (err: Error) => {
        logger.error('Error syncing engagements for program', err)
        if (onError) onError(err)
      }
    }
  )

  return { ...mutation, syncEngagementsForProgram: mutation.mutate }
}
